import { ChemistrySample } from "@/pages/caster/models/ElementData";
import { StrandTimeSeriesData } from "@/pages/caster/models/StrandSpeed";
import { getCatalogMechanicalTests } from "@/pages/laboratory/repositories/CatalogMechanicalTestRepository";
import { getCatalogResultSources } from "@/pages/laboratory/repositories/CatalogResultSourceRepository";
import { getMechanicalSamples } from "@/pages/laboratory/repositories/MechanicalSampleRepository";
import type { Entry } from "@dexteel/mesf-core";
import { MESApiService, getMomentTz, spParameter } from "@dexteel/mesf-core";
import {
	Addition,
	CasterProcessData,
	Charge,
	EAFProcessData,
	HeatData,
	HeatReportDelay,
	Heating,
	Phase,
	Probe,
} from "../models/HeatReportModels";

interface Props {
	HeatId: number | null;
}
const moment = getMomentTz();

export const getHeatReport = async ({ HeatId }: Props) => {
	const sqlParameters = [];
	sqlParameters.push({ name: "HeatId", value: HeatId });

	const apiService = new MESApiService();
	const resp = await apiService.callV2("[SWV].[GetHeatReport]", sqlParameters);
	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map(
			(row: HeatData) => ({
				...row,
				TapDate: row.TapDate ? moment.utc(row.TapDate).toDate() : null,
				OpenDate: row.OpenDate ? moment.utc(row.OpenDate).toDate() : null,
			}),
		);
	}
	return resp;
};

export const getEAFProcessReport = async ({
	ProcessId,
}: { ProcessId: number }) => {
	const sqlParameters = [];
	sqlParameters.push({ name: "ProcessId", value: ProcessId });

	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[EAF].[GetProcessHeatReport]",
		sqlParameters,
	);
	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map(
			(row: EAFProcessData) => ({
				...row,
				Start: row.Start ? moment.utc(row.Start).toDate() : null,
				End: row.End ? moment.utc(row.End).toDate() : null,
				StartTap: row.StartTap ? moment.utc(row.StartTap).toDate() : null,
			}),
		);
		resp.data.tables[1].rows = resp.data.tables[1].rows.map(
			(row: Addition) => ({
				...row,
				Timestamp: row.Timestamp ? moment.utc(row.Timestamp).toDate() : null,
			}),
		);
		resp.data.tables[2].rows = resp.data.tables[2].rows.map((row: Probe) => ({
			...row,
			Timestamp: row.Timestamp ? moment.utc(row.Timestamp).toDate() : null,
		}));
		resp.data.tables[3].rows = resp.data.tables[3].rows.map((row: Charge) => ({
			...row,
			Timestamp: row.Timestamp ? moment.utc(row.Timestamp).toDate() : null,
		}));
		resp.data.tables[4].rows = resp.data.tables[4].rows.map((row: Heating) => ({
			...row,
			Start: row.Start ? moment.utc(row.Start).toDate() : null,
			End: row.End ? moment.utc(row.End).toDate() : null,
		}));
		resp.data.tables[5].rows = resp.data.tables[5].rows.map((row: Phase) => ({
			...row,
			Start: row.Start ? moment.utc(row.Start).toDate() : null,
			End: row.End ? moment.utc(row.End).toDate() : null,
		}));
		resp.data.tables[6].rows = resp.data.tables[6].rows.map(
			(row: HeatReportDelay) => ({
				...row,
				Start: row.Start ? moment.utc(row.Start).toDate() : null,
				End: row.End ? moment.utc(row.End).toDate() : null,
			}),
		);
		resp.data.tables[7].rows = resp.data.tables[7].rows.map((row: Entry) => ({
			...row,
			CreateTimestamp: row.CreateTimestamp
				? moment.utc(row.CreateTimestamp).toDate()
				: null,
			UpdateTimestamp: row.UpdateTimestamp
				? moment.utc(row.UpdateTimestamp).toDate()
				: null,
		}));
	}
	return resp;
};

export const getCASProcessReport = async ({
	ProcessId,
}: { ProcessId: number }) => {
	const sqlParameters = [];
	sqlParameters.push({ name: "ProcessId", value: ProcessId });

	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[CAS].[GetProcessHeatReport]",
		sqlParameters,
	);
	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map(
			(row: CasterProcessData) => ({
				...row,
				LadleOpen: row.LadleOpen ? moment.utc(row.LadleOpen).toDate() : null,
				LadleClose: row.LadleClose ? moment.utc(row.LadleClose).toDate() : null,
			}),
		);
		resp.data.tables[2].rows = resp.data.tables[2].rows.map((row: Probe) => ({
			...row,
			Timestamp: row.Timestamp ? moment.utc(row.Timestamp).toDate() : null,
		}));
		resp.data.tables[5].rows = resp.data.tables[5].rows.map((row: Entry) => ({
			...row,
			CreateTimestamp: row.CreateTimestamp
				? moment.utc(row.CreateTimestamp).toDate()
				: null,
		}));
	}
	return resp;
};

export const getHeatReportChemistrySamples = async ({
	heatId,
}: { heatId: number | null }) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "HeatId", value: heatId });

	const resp = await apiService.callV2(
		"[LAB].[GetHeatReportChemistrySample]",
		parameters,
	);
	if (resp.ok) {
		resp.data.tables[1].rows = resp.data.tables[1].rows.map(
			(row: ChemistrySample) => ({
				...row,
				Reception: row.Reception ? moment.utc(row.Reception).toDate() : null,
			}),
		);
	}
	return resp;
};

export const getEAFValuesByProcessId = async ({
	ProcessId,
	OffsetMinutes = 40,
}: { ProcessId: number; OffsetMinutes?: number }) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProcessId", value: ProcessId });
	parameters.push({ name: "OffsetMinutes", value: OffsetMinutes });

	const resp = await apiService.callV2(
		"[SWV].[GetEAFValuesByProcessId]",
		parameters,
	);
	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map(
			(row: StrandTimeSeriesData) => ({
				...row,
				Datetime: row.Datetime ? moment.utc(row.Datetime).toDate() : null,
			}),
		);
	}
	return resp;
};

export const getMechanicalTestDataForHeat = async ({
	heatId,
}: { heatId: number }) => {
	// Get mechanical test samples for the heat
	const samplesResp = await getMechanicalSamples(heatId, "V"); // Only valid samples

	// Get catalog data in parallel
	const [testsResp, sourcesResp] = await Promise.all([
		getCatalogMechanicalTests(),
		getCatalogResultSources(),
	]);

	return {
		samples: samplesResp,
		tests: testsResp,
		sources: sourcesResp,
	};
};
